/**
 * Shared month-selection hook for board + inbox.
 *
 * Source-of-truth precedence:
 *   1. URL ?month=YYYY-MM     — wins; canonical for shared/refreshed links.
 *   2. sessionStorage         — survives navigation between /board and /inbox.
 *   3. currentMonth()         — first ever visit fallback.
 *
 * Setter writes BOTH (URL replace + sessionStorage) so the next page that
 * reads — even via a different route — sees the same value.
 */
import { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const STORAGE_KEY = "sibit:selectedMonth";
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function readSession(): string | null {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY);
    return v && MONTH_RE.test(v) ? v : null;
  } catch {
    return null;
  }
}

function writeSession(m: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, m);
  } catch {
    // sessionStorage may be unavailable (private mode etc.) — silent fallback
  }
}

export function useSelectedMonth(): { month: string; setMonth: (m: string) => void } {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlMonth = searchParams.get("month");
  const initial =
    (urlMonth && MONTH_RE.test(urlMonth) && urlMonth) ||
    readSession() ||
    currentMonth();
  const [month, setMonthState] = useState<string>(initial);

  // If we adopted from session/default and URL was empty, sync URL once.
  // If URL had a valid month, also persist to session so the other page sees it.
  useEffect(() => {
    if (urlMonth !== month) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("month", month);
          return next;
        },
        { replace: true },
      );
    }
    writeSession(month);
    // Run once on mount; subsequent setMonth calls handle their own writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setMonth = useCallback(
    (m: string) => {
      if (!MONTH_RE.test(m)) return;
      setMonthState(m);
      writeSession(m);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("month", m);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { month, setMonth };
}
